import * as React from 'react';
import * as Yup from 'yup';

import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { Formik, FormikActions, FormikProps } from 'formik';
import { REVENUE_RANGES, formatRevenueRange, fromPhoneE164, toPhoneE164 } from 'common/utils/format';
// import { mergeFields, mergeAddress } from 'common/utils/form';
import { mergeFields, processApiError, mergeAddress } from 'common/utils/form';

import { AppState } from 'common/models/AppState';
import { FormAddressInput } from 'common/components/form/FormAddressInput';
import { FormChapterIdSelect } from 'common/components/form/FormChapterIdSelect';
import { Member } from 'common/api/models/Member';
import { MemberApi } from '../../common/api/resources/MemberApi';
import { addressValidationSchemaFactory } from '../../common/utils/form';
import { Analytics } from 'common/utils/Analytics';
import { FormInput } from 'common/components/form/FormInput';
import { FormInputArray } from 'common/components/form/FormInputArray';
import { FormCheckInputArray } from 'common/components/form/FormCheckInputArray';
import { ACCOUNT_MANAGER_NAMES, SEGMENT_OPTIONS, SUBSEGMENT_OPTIONS } from 'common/utils/memberConstants';

interface OrgsDialogProps {
  org?: Member;
  appState: AppState;
  onSave: (id: string) => void;
  onCancel: () => void;
  parentOrg?: Member;
}

class OrgsFormValues {
  name = '';
  email = '';
  website = '';
  workPhone = '';
  // tslint:disable:variable-name
  work_street1 = '';
  work_street2 = '';
  work_city = '';
  work_state = '';
  work_postal = '';
  work_country = '';
  billing_street1 = '';
  billing_street2 = '';
  billing_city = '';
  billing_state = '';
  billing_postal = '';
  billing_country = '';
  shipping_street1 = '';
  shipping_street2 = '';
  shipping_city = '';
  shipping_state = '';
  shipping_postal = '';
  shipping_country = '';
  // tslint:enable:variable-name
  status = 'ACTIVE';
  subtype = 'COMPANY';
  segment = 'NONE';
  subsegment = 'NONE';
  ceuProvider = '';
  revenueRange = '';
  joinDate = '';
  chapterId = '';
  foundingSchool = '';
  programType = 'ONLINE';
  programsWebAddress = '';
  programsOffered: Array<string> = [];
  degreesOffered: Array<Member.DegreesOffered> = [];
  akbdApproved = '';
  jobsFeatured = '';
  social = {
    linkedin: '',
  };
  staffCountRange = '';
  signupSource = '';
  accountManager = '';
  naicsCode = '';
  sicCode = '';
  dnbRevenue = '';
  isGlobalConnect = 'NO';
}
export interface OrgDialogState {
  formDisabled: boolean;
}
export class OrgsDialog extends React.Component<OrgsDialogProps, OrgDialogState> {
  state: OrgDialogState = { formDisabled: false };
  validationSchema = Yup.object().shape({
    name: Yup.string().required('Required'),
    email: Yup.string()
      .email()
      .lowercase()
      .required('Required'),
    workPhone: Yup.string()
      .required('Required')
      .matches(/^[0-9 \-+]*$/, 'Invalid phone format'),
    ...addressValidationSchemaFactory('work', true),
    ...addressValidationSchemaFactory('billing'),
    ...addressValidationSchemaFactory('shipping'),
    joinDate: Yup.date('Invalid date')
      .min('1963-01-01', 'Too far in the past')
      .max(new Date(), 'Must be in the past'),
  });

  initialValues = () => {
    return this.props.org
      ? mergeFields(new OrgsFormValues(), {
          ...this.props.org,
          ...mergeAddress(this.props.org.workAddress, 'work'),
          ...mergeAddress(this.props.org.billingAddress, 'billing'),
          ...mergeAddress(this.props.org.shippingAddress, 'shipping'),
          workPhone: fromPhoneE164(this.props.org.workPhone),
          ...this.props.org.vars,
        })
      : new OrgsFormValues();
  };

  handleSubmit = (values: OrgsFormValues, actions: FormikActions<OrgsFormValues>) => {
    // console.log(values);

    this.setState({ formDisabled: true });

    const orgData: NullableValues<Member> = {
      name: values.name,
      email: values.email,
      website: values.website,
      social: {
        linkedin: values.social.linkedin,
      },
      workPhone: toPhoneE164(values.workPhone),
      workAddress:
        values.work_street1 || values.work_city || values.work_state || values.work_country || values.work_postal
          ? {
              street1: values.work_street1,
              street2: values.work_street2,
              city: values.work_city,
              state: values.work_state,
              country: values.work_country,
              postal: values.work_postal,
            }
          : null,
      billingAddress:
        values.billing_street1 ||
        values.billing_city ||
        values.billing_state ||
        values.billing_country ||
        values.billing_postal
          ? {
              street1: values.billing_street1,
              street2: values.billing_street2,
              city: values.billing_city,
              state: values.billing_state,
              country: values.billing_country,
              postal: values.billing_postal,
            }
          : null,
      shippingAddress:
        values.shipping_street1 ||
        values.shipping_city ||
        values.shipping_state ||
        values.shipping_country ||
        values.shipping_postal
          ? {
              street1: values.shipping_street1,
              street2: values.shipping_street2,
              city: values.shipping_city,
              state: values.shipping_state,
              country: values.shipping_country,
              postal: values.shipping_postal,
            }
          : null,
      status: Member.Status[values.status],
      joinDate: values.joinDate ? values.joinDate : undefined,
      revenueRange: values.revenueRange,
      foundingSchool: values.foundingSchool === '1',
      programType: Member.ProgramType[values.programType],
      programsWebAddress: values.programsWebAddress,
      programsOffered: values.programsOffered,
      degreesOffered: values.degreesOffered,
      akbdApproved: values.akbdApproved === '1',
      vars: {
        ceuProvider: values.ceuProvider === '1',
      },
      chapterId: values.chapterId,
      segment: values.segment ? values.segment : 'NONE',
      subsegment: values.subsegment ? values.subsegment : 'NONE',
      tagIds: [],
      awards: [],
      jobsFeatured: values.jobsFeatured === '1',
      staffCountRange: Member.StaffCountRange[values.staffCountRange],
      signupSource: values.signupSource ? Member.SignupSource[values.signupSource] : undefined,
      accountManager: values.accountManager,
      naicsCode: values.naicsCode,
      sicCode: values.sicCode,
      dnbRevenue: values.dnbRevenue,
      isGlobalConnect: values.isGlobalConnect === '1',
    };
    if (this.props.parentOrg) {
      orgData.parentOrgId = this.props.parentOrg.id;
      delete orgData.revenueRange;
    }
    // if new, not an edit
    if (!this.props.org || !this.props.org.id) {
      orgData.type = Member.Type.ORG;
      orgData.visibility = Member.Visibility.PUBLIC;
    } else {
      // update, don't send tagIds or awards
      delete orgData.tagIds;
      delete orgData.awards;
    }
    const call =
      this.props.org && this.props.org.id
        ? MemberApi.updateMember(this.props.org.id, orgData as Member)
        : MemberApi.createMember(orgData as Member);
    const analyticsEvent = this.props.org && this.props.org.id ? Analytics.member.update : Analytics.member.create;

    call.then(
      res => {
        this.props.onSave(this.props.org && this.props.org.id ? this.props.org.id! : (res as any).id);
        analyticsEvent();
        actions.setSubmitting(false);
        actions.setStatus(undefined);
        this.setState({ formDisabled: false });
      },
      err => {
        processApiError(err, actions);
      }
    );
  };

  render() {
    const title = this.props.org && this.props.org.id ? 'Edit Org' : 'Add Org';
    const isBranch = (this.props.org && this.props.org.parentOrgId) || this.props.parentOrg;
    const isSchool =
      this.props.org && this.props.org.memberType && this.props.org.memberType === Member.MemberType.SCHOOL;
    return (
      <Modal size="lg" isOpen={!!this.props.org} toggle={this.props.onCancel} keyboard={true}>
        <Formik
          initialValues={this.initialValues()}
          validationSchema={this.validationSchema}
          onSubmit={this.handleSubmit}
          render={(form: FormikProps<OrgsFormValues>) => (
            <form onSubmit={form.handleSubmit} noValidate={true}>
              <ModalHeader toggle={this.props.onCancel}>{title}</ModalHeader>
              <ModalBody>
                <div className="row">
                  <div className="col">
                    <FormInput name="name" id="name" label="Name" autoComplete="off" autoFocus={true} />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormInput type="email" id="email" name="email" autoComplete="off" label="Email" />
                  </div>
                  <div className="col">
                    <FormInput id="workPhone" name="workPhone" autoComplete="off" label="Phone" />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormInput type="url" id="website" name="website" autoComplete="off" label="Website" />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormInput
                      type="url"
                      id="linkedin"
                      name="social.linkedin"
                      placeholder="in/your-profile"
                      autoComplete="off"
                      label="LinkedIn Profile"
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col col-12 col-md-6">
                    <FormAddressInput form={form} prefix="work" label="Work Address" />
                  </div>
                  <div className="col col-12 col-md-6">
                    <FormAddressInput form={form} prefix="shipping" label="Mailing Address" />
                  </div>
                </div>
                <div className="row">
                  <div className="col col-12 col-md-6">
                    <FormAddressInput form={form} prefix="billing" label="Billing Address" />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormInput tag="select" id="status" name="status" label="Status">
                      <option value="ACTIVE">Active</option>
                      <option value="EXPIRED">Expired</option>
                      <option value="INACTIVE">Inactive</option>
                    </FormInput>
                  </div>
                  <div className="col">
                    <FormInput tag="select" name="ceuProvider" id="ceuProvider" label="CEU Provider">
                      <option value="0">No</option>
                      <option value="1">Yes</option>
                    </FormInput>
                  </div>
                </div>
                <div className="row">
                  <div className="col col-6">
                    <FormChapterIdSelect appState={this.props.appState} form={form} />
                  </div>
                  {!isBranch && (
                    <div className="col col-3">
                      <FormInput tag="select" name="revenueRange" id="revenueRange" label="Revenue">
                        {REVENUE_RANGES.map(range => (
                          <option key={range} value={String(range)}>
                            {formatRevenueRange(range)}
                          </option>
                        ))}
                      </FormInput>
                    </div>
                  )}
                  <div className="col col-3">
                    <FormInput type="date" id="joinDate" name="joinDate" label="Join Date" />
                  </div>
                </div>
                <div className="row">
                  <div className="col col-6">
                    <FormInput tag="select" name="segment" id="segment" label="Segment">
                      {SEGMENT_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </FormInput>
                  </div>
                  <div className="col col-6">
                    <FormInput tag="select" name="subsegment" id="subsegment" label="Subsegment">
                      <option value="NONE">No Subsegment Selected</option>
                      {SUBSEGMENT_OPTIONS[form.values.segment] &&
                        SUBSEGMENT_OPTIONS[form.values.segment].map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                    </FormInput>
                  </div>
                </div>
                <div className="row">
                  <div className="col col-3">
                    <FormInput type="number" id="sicCode" name="sicCode" label="SIC Code" />
                  </div>
                  <div className="col col-3">
                    <FormInput type="number" id="naicsCode" name="naicsCode" label="NAICS Code" />
                  </div>
                  {!isBranch && (
                    <div className="col col-6">
                      <FormInput type="text" name="dnbRevenue" id="dnbRevenue" label="D&B Revenue" />
                    </div>
                  )}
                </div>
                <div className="row">
                  <div className="col col-6">
                    <FormInput tag="select" name="jobsFeatured" id="jobsFeatured" label="Featured Job">
                      <option value="0">No</option>
                      <option value="1">Yes</option>
                    </FormInput>
                  </div>
                  <div className="col col-6">
                    <FormInput tag="select" name="staffCountRange" id="staffCountRange" label="Staff Count Range">
                      <option value="''">None</option>
                      <option value="MAX_1">1</option>
                      <option value="MAX_10">2-10</option>
                      <option value="MAX_50">11-50</option>
                      <option value="MAX_200">51-200</option>
                      <option value="MAX_500">201-500</option>
                      <option value="MAX_1000">501-1,000</option>
                      <option value="MAX_5000">1,001-5,000</option>
                      <option value="MAX_10000">5,001-10,000</option>
                      <option value="OVER_10000">10,000+</option>
                    </FormInput>
                  </div>
                </div>
                <div className="row">
                  <div className="col col-6">
                    <FormInput tag="select" name="signupSource" id="signupSource" label="Signup Source">
                      <option value="ONLINE">Online</option>
                      <option value="CHAPTER">Chapter</option>
                      <option value="SALES">Sales</option>
                      <option value="REACTIVATION">Reactivation</option>
                      <option value="EVENT">Event</option>
                    </FormInput>
                  </div>
                  <div className="col col-6">
                    <FormInput tag="select" name="accountManager" id="accountManager" label="Account Manager">
                      <option value="">None</option>
                      {ACCOUNT_MANAGER_NAMES.map(name => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </FormInput>
                  </div>
                </div>
                <div className="row">
                  <div className="col col-6">
                    <FormInput tag="select" name="isGlobalConnect" id="isGlobalConnect" label="Global Connect">
                      <option value="0">No</option>
                      <option value="1">Yes</option>
                    </FormInput>
                  </div>
                </div>
                {isSchool && (
                  <>
                    <div className="row">
                      <div className="col">
                        <FormInput tag="select" name="foundingSchool" id="foundingSchool" label="Founding School">
                          <option value="0">No</option>
                          <option value="1">Yes</option>
                        </FormInput>
                      </div>
                      <div className="col">
                        <FormInputArray
                          id="programsOffered"
                          name="programsOffered"
                          label="Programs Offered"
                          nameValue={form.values.programsOffered}
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col">
                        <FormInput tag="select" name="programType" id="programType" label="Program Type">
                          <option value="IN_PERSON">In Person</option>
                          <option value="ONLINE">Online</option>
                          <option value="IN_PERSON_ONLINE">In Person/Online</option>
                        </FormInput>
                      </div>
                      <div className="col">
                        <FormInput
                          name="programsWebAddress"
                          id="programsWebAddress"
                          label="Program Web Address"
                          autoComplete="off"
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col">
                        <FormInput
                          tag="select"
                          name="akbdApproved"
                          id="akbdApproved"
                          label="Approved AKBD Testing Center"
                        >
                          <option value="0">No</option>
                          <option value="1">Yes</option>
                        </FormInput>
                      </div>
                      <div className="col">
                        <FormCheckInputArray
                          name="degreesOffered"
                          id="degreesOffered"
                          label="Degrees Offered"
                          form={form}
                          checkInputs={[
                            { value: 'DIPLOMA', title: 'Diploma' },
                            { value: 'CERTIFICATE', title: 'Certificate' },
                            { value: 'BACHELOR', title: 'Bachelor' },
                            { value: 'MASTER', title: 'Master' },
                            { value: 'DOCTOR', title: 'Doctor' },
                          ]}
                        />
                      </div>
                    </div>
                  </>
                )}

                {form.status && <div className="alert alert-danger">{form.status}</div>}
              </ModalBody>
              <ModalFooter>
                <div>
                  <Button color="secondary" onClick={this.props.onCancel}>
                    Cancel
                  </Button>{' '}
                  <Button color="primary" type="submit" disabled={this!.state!.formDisabled}>
                    Save
                  </Button>
                </div>
              </ModalFooter>
            </form>
          )}
        />
      </Modal>
    );
  }
}



// WEBPACK FOOTER //
// ./src/screens/Orgs/OrgsDialog.tsx